import { APP_INITIALIZER, Inject, NgModule, NgZone, PLATFORM_ID, TransferState } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withFetch } from '@angular/common/http';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateLoaderComEstadoDoServidor } from './i18n/traducoes-do-servidor';
import { NgIdleModule } from '@ng-idle/core';
import {
    RestaurantService,
    StartupService,
    TableService,
    ApiService,
    ThemeService,
    CreditCardService,
    CartRecoverService
} from './services';
import { AuthenticationService } from './services/authentication.service';
import { comPrazo } from '../shared/utils/com-prazo';
import { marcarFaseDoBoot } from './services/telemetry/boot-watchdog';

/**
 * Prazo do carregamento do cardápio no boot.
 *
 * Folgado de propósito: em 3G ruim um cardápio grande passa dos 8 s, e cortar um
 * boot honesto seria pior que a doença — o usuário veria "não deu tempo" no
 * lugar do cardápio que estava quase chegando. O alvo aqui é a requisição que
 * NUNCA volta, não a lenta.
 */
export const PRAZO_CARDAPIO_MS = 15000;
import { TokenInterceptor } from './interceptor/token.interceptor';
import { ShoppingCartService } from './services/shoppingCart.service';
import { idleTimeoutService } from './services/idleTimeout.service';
import { LaddaModule } from 'angular2-ladda';
import { NavService } from './services/nav.service';
import { OrderService } from './services/order.service';
import { Events } from './services/events.service';
import { DebugModeService } from './services/debugMode.service';
import { CartGuardService } from './services/cart-guard.service';
import { DataGuardService } from './services/data-guard.service';
import { FingerprintService } from './services/fingerprint.service';
import { KeenTrackingService } from './services/keenTracking.service';
import { CloudwatchMetricsService } from './services/cloudwatchMetrics.service';
import { SocialAuth } from './services/SocialAuth.service';
import { SocialLoginModule, SocialAuthServiceConfig, GoogleLoginProvider, SOCIAL_AUTH_CONFIG } from '@abacritt/angularx-social-login';
import { AutoCompletePhoneNumberService } from './services/autoCompletePhoneNumber.service';
import { isDesignSystemBoot } from '../shared/design-system-availability';

/**
 * O loader usa primeiro as traduções que o servidor embutiu (SSR) e só então a
 * rede — sem SSR a TransferState vem vazia e o comportamento é o de sempre.
 * Ver `i18n/traducoes-do-servidor.ts`.
 */
export function createTranslateLoader(http: HttpClient, estado: TransferState) {
    return new TranslateLoaderComEstadoDoServidor(http, estado);
}

/**
 * Transporte do HttpClient: `fetch` SÓ no server, XHR no browser.
 *
 * O `withFetch()` entrou em 06/07/2026 pelo commit de SSR (30294a4d) — o
 * prerender roda em Node, onde `XMLHttpRequest` não existe — e chegou a
 * produção no deploy de 04/08 valendo também para o browser.
 *
 * No browser ele trouxe uma classe de erro que não existia: `TypeError` de rede
 * CRU chegando ao ErrorHandler global, com as mensagens que só a Fetch API
 * produz (`Load failed` no WebKit, `Failed to fetch` no V8 — o XHR nunca as
 * emite). Medido no Athena: ZERO absoluto de 29/07 a 03/08, depois 228 devices
 * no dia 04/08 e ~350-900/dia desde então, com 82,6% na rota `/payment` e mais
 * 8,7% em `/payment/success`. Vale ~70% de toda a subida de TypeError do período.
 *
 * Por qual linha do `FetchBackend` o erro escapa, não sabemos: o `handle()` faz
 * `.then(noop, e => observer.error(new HttpErrorResponse({error: e})))`, que em
 * leitura deveria embrulhar inclusive a rejeição da leitura em streaming. A
 * atribuição aqui NÃO vem do mecanismo e sim da eliminação da fonte — aquelas
 * mensagens só podem vir de `fetch`, e os únicos outros `fetch` do bundle rodam
 * no boot (99,1% dos devices que erram já viram o cardápio, então não são eles).
 *
 * Condicional e não remoção: os caminhos de SSR seguem vivos no código
 * (`acquireCardapio` tem ramo `!isPlatformBrowser`) e o gate de prerender pode
 * voltar. Remover deixaria a armadilha de quem religar o SSR descobrir longe
 * daqui que XHR não existe no Node.
 */
export function featuresDeTransporteHttp(noServer = typeof window === 'undefined') {
    return noServer ? [withFetch()] : [];
}

@NgModule({ exports: [
        TranslateModule
    ],
    declarations: [], imports: [CommonModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: createTranslateLoader,
                deps: [HttpClient, TransferState],
            },
        }),
        NgIdleModule.forRoot(),
        LaddaModule.forRoot({
            style: "contract",
            spinnerSize: 40,
            spinnerColor: "red",
            spinnerLines: 12
        }),
        SocialLoginModule], providers: [
        TranslateModule,
        CartRecoverService,
        ApiService,
        DebugModeService,
        RestaurantService,
        TableService,
        ShoppingCartService,
        ThemeService,
        idleTimeoutService,
        StartupService,
        AuthenticationService,
        NavService,
        OrderService,
        CartGuardService,
        DataGuardService,
        CreditCardService,
        FingerprintService,
        KeenTrackingService,
        CloudwatchMetricsService,
        SocialAuth,
        {
            provide: APP_INITIALIZER,
            // A página de design system (só local) não pertence a nenhum restaurante:
            // pular a carga evita um GET /restaurant/data/v2/design-system que sempre falha.
            //
            // O PRAZO aqui não é defensividade genérica — é o invariante mais
            // importante do boot. O Angular só faz o bootstrap depois que TODO
            // APP_INITIALIZER resolve, e `loadByURL` faz requisições sem timeout
            // (não há interceptor de timeout no app). Uma delas pendurando —
            // nem resolve, nem rejeita — deixava o esqueleto do index.html
            // ("Carregando o cardápio…") na tela PARA SEMPRE, sem erro e sem
            // telemetria. É o sintoma do MP-569. Com o prazo o app sempre sobe:
            // o `menu.page` já sabe lidar com restaurante ausente, e agora
            // consegue distinguir "não existe" de "não deu tempo".
            useFactory: (startupService: StartupService, restaurantService: RestaurantService) => () => {
                if (isDesignSystemBoot()) return Promise.resolve(null);
                marcarFaseDoBoot('cardapio');
                return comPrazo(
                    restaurantService.loadByURL(startupService.getRestaurantURL()),
                    PRAZO_CARDAPIO_MS,
                ).then((r) => {
                    marcarFaseDoBoot('bootstrap');
                    if (r.estourou) restaurantService.falhaDeCarga = 'prazo';
                    // `estourou`, não `valor === undefined`: `loadByURL` resolve
                    // com `null` por caminhos legítimos, e as duas situações
                    // pedem leituras diferentes de quem vier ler isto depois.
                    return r.estourou ? null : (r.valor === undefined ? null : r.valor);
                });
            },
            deps: [StartupService, RestaurantService],
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TokenInterceptor,
            multi: true
        },
        {
            provide: SOCIAL_AUTH_CONFIG,
            useValue: {
                autoLogin: false,
                providers: [
                    {
                        id: GoogleLoginProvider.PROVIDER_ID,
                        provider: new GoogleLoginProvider('982958682059-e2i6tqngt5ds6rjb5ec1ba7ndtkn6hg7.apps.googleusercontent.com', {
                            // GIS (Google Identity Services): sem One Tap para manter a UX
                            // do botão próprio; scopes explícitos garantem que o access_token
                            // continue servindo para o userinfo no backend (contrato antigo).
                            oneTapEnabled: false,
                            scopes: 'openid profile email',
                        })
                    }
                ]
            } as SocialAuthServiceConfig,
        },
        AutoCompletePhoneNumberService,
        provideHttpClient(withInterceptorsFromDi(), ...featuresDeTransporteHttp())
    ] })
export class CoreModule {
    constructor(
        private events: Events,
        private debugModeService: DebugModeService,
        @Inject(PLATFORM_ID) platformId: Object,
        private zone: NgZone,
    ) {
        // Todo o bloco (resize, debugMode) é browser-only.
        if (!isPlatformBrowser(platformId)) return;
        // Ionic Platform removido: platform.ready() ~ imediato na web; platform.resize
        // vira o listener nativo de 'resize'. Registrado fora da zone (evita CD do
        // próprio listener) e reentra só no publish (fiel ao platform.resize, que rodava
        // na zone).
        Promise.resolve().then(() => {

            this.zone.runOutsideAngular(() => {
                window.addEventListener('resize', () => {
                    this.zone.run(() => this.events.publish('windowResized'));
                }, { passive: true });
            });

            if(this.debugModeService.getMode()) this.debugModeService.debugMessage();

            window['debugMode'] = () => {
                this.debugModeService.debug();
            };

            // O Google Maps saía daqui, em toda sessão (208 kB no meio do boot). Agora
            // é sob demanda: `ensureGoogleMaps()` em shared/utils/google-maps-loader.ts,
            // chamado pelas cinco telas que realmente desenham mapa.
        });
    }
}